import { ApolloClient, InMemoryCache, ApolloLink } from '@apollo/client';
import { createLink } from 'apollo-absinthe-upload-link';
import { redirect } from 'react-router-dom';
import { setContext } from '@apollo/client/link/context';
import { getLanguageFromPath } from '@/utils/utils';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';

export enum SCHEMAS {
	ACCOUNTS = 'accounts',
	AUTHENTICATION = 'authentication',
	ONBOARDING = 'onboarding',
	MICROCREDITS = 'credit-requests',
	VAN_CREDITS = 'credit-requests/np-van-credits',
	ONBOARDING_LEGAL = 'onboarding/legal-person'
}

const languageHeaderConfig = {
	es: 'es-MX',
	en: 'en'
};

const BASE_URL = import.meta.env.VITE_GRAPHQL_API_BASE_URL;

export const ENDPOINTS = {
	[SCHEMAS.ACCOUNTS]: `${BASE_URL}/${SCHEMAS.ACCOUNTS}`,
	[SCHEMAS.AUTHENTICATION]: `${BASE_URL}/${SCHEMAS.AUTHENTICATION}`,
	[SCHEMAS.ONBOARDING]: `${BASE_URL}/${SCHEMAS.ONBOARDING}`,
	[SCHEMAS.MICROCREDITS]: `${BASE_URL}/${SCHEMAS.MICROCREDITS}`,
	[SCHEMAS.VAN_CREDITS]: `${BASE_URL}/${SCHEMAS.VAN_CREDITS}`,
	[SCHEMAS.ONBOARDING_LEGAL]: `${BASE_URL}/${SCHEMAS.ONBOARDING_LEGAL}`
} as const;

type EndpointKey = keyof typeof ENDPOINTS;

// Helper function to format the query string
const formatGraphQLQuery = (query: string): string => {
	return query.replace(/\\n/g, '\n').replace(/\\t/g, '').replace(/\s+/g, ' ').trim();
};

// Helper function to format operation details for logging
const formatOperationDetails = (operation: any) => {
	return {
		operationName: operation.operationName,
		variables: operation.variables,
		query: operation.query?.loc?.source?.body
			? formatGraphQLQuery(operation.query.loc.source.body)
			: 'Query not available'
	};
};

const createUploadLink = (endpointKey: EndpointKey): ApolloLink => {
	return createLink({
		uri: ENDPOINTS[endpointKey],
		credentials: 'include',
		fetchOptions: {
			mode: 'cors'
		}
	});
};

// Authentication Link
const authLink = setContext((operation, { headers }) => {
	// operations that should not include the auth token
	const publicOperations = ['SignIn', 'RecoverPassword', 'RequestEmailVerification'];
	const language = getLanguageFromPath(
		window.location.pathname
	) as keyof typeof languageHeaderConfig;

	let tokenValue = null;

	if (operation.operationName === 'AcceptInvitation') {
		const urlParams = new URLSearchParams(window.location.search);
		const tokenFromUrl = urlParams.get('token');
		if (tokenFromUrl) {
			tokenValue = tokenFromUrl;
		}
	}

	if (!tokenValue) {
		const tokenString = sessionStorage.getItem('token');
		const token = tokenString ? JSON.parse(tokenString) : null;
		tokenValue = token?.state?.token || null;
	}

	const shouldExcludeToken = operation.operationName
		? publicOperations.includes(operation.operationName)
		: false;

	return {
		headers: {
			...headers,
			'x-csrf-token': 'WXwHOigPAj8zT3h1BCAcdQA0Jw5GeWMy0NrRglWX_8-B2vz9ZG_cwO9e',
			'Accept-Language': languageHeaderConfig[language] || 'es-MX',
			...(shouldExcludeToken ? {} : { authorization: tokenValue ? `Bearer ${tokenValue}` : '' })
		}
	};
});

// Create logging link for specific endpoint
const createLoggingLink = (endpointKey: EndpointKey) => {
	return new ApolloLink((operation, forward) => {
		const operationType =
			operation.query.definitions[0]?.kind === 'OperationDefinition'
				? operation.query.definitions[0].operation
				: 'unknown';

		console.log(
			`\n🚀 Starting ${operationType.toUpperCase()} (${endpointKey}): ${operation.operationName}`
		);
		console.log('📝 Operation details:');
		console.log(JSON.stringify(formatOperationDetails(operation), null, 2));

		return forward(operation).map((response) => {
			console.log(
				`\n✅ Response for ${operationType.toUpperCase()} (${endpointKey}): ${operation.operationName}`
			);
			console.log('📦 Response data:');
			console.log(JSON.stringify(response.data, null, 2));
			return response;
		});
	});
};

// Error handling link
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
	const operationType =
		operation.query.definitions[0]?.kind === 'OperationDefinition'
			? operation.query.definitions[0].operation
			: 'unknown';

	if (graphQLErrors) {
		graphQLErrors.forEach(({ message, locations, path, extensions }) => {
			const errorMessage =
				`\n❌ [GraphQL error]: ${operationType.toUpperCase()}: ${operation.operationName}` +
				`\nMessage: ${message}` +
				`\nLocation: ${JSON.stringify(locations)}` +
				`\nPath: ${path}`;

			if (extensions?.error_code === 'INVALID_BEARER_TOKEN') {
				console.error(`🚫 Invalid token detected - initiating logout process`);
				sessionStorage.removeItem('token');
				return redirect(`/es/signin/?expired=true`);
			} else {
				console.error(errorMessage);
				Sentry.captureException(errorMessage);
			}
		});
	}

	if (networkError) {
		const errorMessage = `\n🔴 [Network error in ${operationType.toUpperCase()} ${operation.operationName}]: ${networkError}`;
		console.error(errorMessage);
		Sentry.captureException(errorMessage);
	}
});

// Create Apollo Client for specific endpoint
const createApolloClient = (endpointKey: EndpointKey) => {
	const httpLink = createUploadLink(endpointKey);
	const loggingLink = createLoggingLink(endpointKey);

	return new ApolloClient({
		link: ApolloLink.from([errorLink, loggingLink, authLink, httpLink]),
		cache: new InMemoryCache(),
		defaultOptions: {
			watchQuery: {
				fetchPolicy: 'network-only',
				errorPolicy: 'ignore'
			},
			query: {
				fetchPolicy: 'network-only',
				errorPolicy: 'all'
			}
		}
	});
};

export const clients = {
	[SCHEMAS.ACCOUNTS]: createApolloClient(SCHEMAS.ACCOUNTS),
	[SCHEMAS.AUTHENTICATION]: createApolloClient(SCHEMAS.AUTHENTICATION),
	[SCHEMAS.ONBOARDING]: createApolloClient(SCHEMAS.ONBOARDING),
	[SCHEMAS.MICROCREDITS]: createApolloClient(SCHEMAS.MICROCREDITS),
	[SCHEMAS.VAN_CREDITS]: createApolloClient(SCHEMAS.VAN_CREDITS),
	[SCHEMAS.ONBOARDING_LEGAL]: createApolloClient(SCHEMAS.ONBOARDING_LEGAL)
} as const;

export const resetAllApolloCaches = async () => {
	try {
		await Promise.all(Object.values(clients).map((client) => client.resetStore()));
		console.log('All Apollo caches reset successfully');
	} catch (error) {
		console.error('Error resetting Apollo caches:', error);
		Sentry.captureException(error);
	}
};

export default clients[SCHEMAS.AUTHENTICATION];
